import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { ArrowRight } from 'lucide-react'

export function LoginForm() {
  const { t } = useTranslation()
  const { user, loading, sendLoginCode, verifyCode, error } = useAuth()
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [sending, setSending] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [sent, setSent] = useState(false)
  const [localError, setLocalError] = useState('')

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-ink"></div>
      </div>
    )
  }

  if (user) {
    return <Navigate to="/" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError('')

    if (!email) {
      setLocalError(t('login.emailRequired'))
      return
    }

    setSending(true)
    const result = await sendLoginCode(email)
    setSending(false)

    if (result.success) {
      setSent(true)
    } else {
      setLocalError(result.error || t('login.sendFailed'))
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError('')

    if (!code) {
      setLocalError(t('login.codeRequired'))
      return
    }

    setVerifying(true)
    const result = await verifyCode(code.trim())
    setVerifying(false)

    if (!result.success) {
      setLocalError(result.error || t('login.verifyFailed'))
    }
    // On success the auth state updates and the `user` guard above redirects.
  }

  const handleUseDifferentEmail = () => {
    setSent(false)
    setCode('')
    setEmail('')
    setLocalError('')
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-paper">
      {/* Painterly hero — same imagery as the marketing site, with a cream veil pulling the
          gallery feel into the auth flow. The copy frame floats over it like the hero card. */}
      <img
        src="/assets/hero1.webp"
        alt=""
        className="pointer-events-none absolute inset-0 h-full w-full object-cover [object-position:65%_center]"
        draggable={false}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'linear-gradient(90deg, rgba(246,241,230,0.92) 0%, rgba(246,241,230,0.78) 32%, rgba(246,241,230,0.45) 60%, rgba(246,241,230,0.18) 100%), linear-gradient(180deg, rgba(246,241,230,0.6) 0%, rgba(246,241,230,0) 25%)',
        }}
      />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-6 py-12 sm:px-10">
        <div className="w-full max-w-[460px]">
          {/* Wordmark — Cormorant caps, museum-label spacing. */}
          <a href="/" className="auvers-logo mb-8" style={{ fontSize: 22, letterSpacing: '0.22em' }}>
            AUVERS
            <span className="product">Sentry Manager</span>
          </a>

          {/* Floating glass copy frame, same pattern as the hero card on the marketing site. */}
          <div className="frame relative rounded-lg p-9 sm:p-10">
            <span className="bracket tl" />
            <span className="bracket tr" />
            <span className="bracket bl" />
            <span className="bracket br" />

            <div className="eyebrow mb-3">
              <span className="grid h-[18px] w-[18px] place-items-center rounded-full bg-gold-soft text-ink">
                <ArrowRight className="h-3 w-3" />
              </span>
              <span className="ml-2.5">{t('login.eyebrow', 'Sign in')}</span>
            </div>

            <h1 className="font-serif text-[40px] font-medium leading-[1.05] tracking-[-0.015em] text-ink">
              {t('login.title')}
            </h1>
            <hr className="gold-rule my-4" />
            <p className="mb-7 max-w-[400px] text-[15px] leading-[1.6] text-ink-soft">
              {t('login.description')}
            </p>

            {error && (
              <div className="mb-4">
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              </div>
            )}

            {sent ? (
              <form onSubmit={handleVerify} className="space-y-5">
                <Alert variant="success">
                  <AlertDescription className="font-sans text-[14px] font-medium">
                    {t('login.checkEmailCode')}
                  </AlertDescription>
                </Alert>
                <p className="text-[14px] leading-[1.55] text-ink-soft">
                  {t('login.sentCodeTo')} <strong className="font-mono text-[13px] tracking-wider text-ink">{email}</strong>
                </p>

                <div className="space-y-2">
                  <Label htmlFor="code">{t('login.codeLabel')}</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder={t('login.codePlaceholder')}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    disabled={verifying}
                    autoFocus
                  />
                </div>

                {(localError || error) && (
                  <Alert variant="destructive">
                    <AlertDescription>{localError || error}</AlertDescription>
                  </Alert>
                )}

                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <Button type="submit" disabled={verifying}>
                    {verifying ? t('login.verifying') : t('login.verify')}
                    <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.6} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="px-0"
                    onClick={handleUseDifferentEmail}
                  >
                    {t('login.differentEmail')}
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('login.emailLabel')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('login.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={sending}
                    autoFocus
                  />
                </div>

                {(localError || error) && (
                  <Alert variant="destructive">
                    <AlertDescription>{localError || error}</AlertDescription>
                  </Alert>
                )}

                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <Button type="submit" disabled={sending}>
                    {sending ? t('login.sending') : t('login.sendCode')}
                    <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.6} />
                  </Button>
                  <LanguageSwitcher />
                </div>
              </form>
            )}
          </div>

          {/* Footer wordmark — paired mono caption gives the page its instrument-on-paper feel. */}
          <p className="mono mt-6 text-[10.5px] uppercase tracking-[0.18em] text-ink-soft">
            on-premise · presence intelligence
          </p>
        </div>
      </div>
    </div>
  )
}
